import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { TrendingUp, CompareArrows, AttachMoney } from '@mui/icons-material';
import { RecipeV2Detail, ComponentSupplierComparison, CostSimulationResult } from '../types';
import { applyRoundAndFormat } from '../../../utils';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import { UserContext } from '../../../components/shared/useUser';

interface CostingTabProps {
  recipe: RecipeV2Detail;
  currencyCode?: string;
}

const CostingTab = ({ recipe, currencyCode }: CostingTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const { hasPermissionTo } = useContext(UserContext);
  const canSimulate = hasPermissionTo(['RecipeV2CostingSimulate']);
  const [selectedComponent, setSelectedComponent] = useState<number>(0);
  const [priceChange, setPriceChange] = useState<number>(10);
  const [simulationResult, setSimulationResult] = useState<CostSimulationResult | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  // Supplier comparison
  const [supplierData, setSupplierData] = useState<ComponentSupplierComparison[]>([]);
  const [supplierLoading, setSupplierLoading] = useState(false);

  // Margin simulation
  const [sellPrice, setSellPrice] = useState<number>(0);

  useEffect(() => {
    setSupplierLoading(true);
    api.getSupplierComparison(recipe.id)
      .then(setSupplierData)
      .catch(() => {})
      .finally(() => setSupplierLoading(false));
  }, [recipe.id, api]);

  const formatCurrency = (value: number) => applyRoundAndFormat(value, currencyCode);

  const totalCost = recipe.components.reduce((sum, c) => sum + c.costPrice * c.quantity, 0);

  const handleSimulate = async () => {
    if (!selectedComponent) return;
    setIsSimulating(true);
    try {
      const result = await api.simulateCost(recipe.id, selectedComponent, priceChange);
      setSimulationResult(result);
    } catch {
      setSimulationResult(null);
    } finally {
      setIsSimulating(false);
    }
  };

  return (
    <Stack spacing={3}>
      {/* Cost breakdown */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('costing.title')}</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('ingredient')}</TableCell>
                <TableCell align="right">{t('costing.unitCost')}</TableCell>
                <TableCell align="right">{t('quantity')}</TableCell>
                <TableCell align="right">{t('costing.lineCost')}</TableCell>
                <TableCell align="right">{t('costing.percentage')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipe.components.map((c) => {
                const lineCost = c.costPrice * c.quantity;
                const pct = (lineCost / totalCost) * 100;
                return (
                  <TableRow key={c.id}>
                    <TableCell>{c.componentDescription}</TableCell>
                    <TableCell align="right">{formatCurrency(c.costPrice)}</TableCell>
                    <TableCell align="right">{c.quantity.toFixed(3)}</TableCell>
                    <TableCell align="right">{formatCurrency(lineCost)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={0.5}>
                        <Typography variant="textSM">{pct.toFixed(1)}%</Typography>
                        <Box
                          sx={{
                            width: Math.max(pct * 0.8, 4),
                            height: 8,
                            borderRadius: 1,
                            bgcolor: pct > 30 ? 'error.main' : pct > 15 ? 'warning.main' : 'success.main',
                          }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={3}>
                  <Typography fontWeight={600}>{t('costing.totalCost')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>{formatCurrency(totalCost)}</Typography>
                </TableCell>
                <TableCell align="right">100%</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={3}>
                  <Typography fontWeight={600}>{t('costing.costPerPortion')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>
                    {formatCurrency(totalCost / recipe.yieldQuantity)}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* What-if simulation */}
      {canSimulate && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Typography variant="textXL"
            gutterBottom>
            <TrendingUp sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('costing.simulateTitle')}
          </Typography>
          <Stack direction="row"
            spacing={2}
            alignItems="center">
            <Select
              size="small"
              value={selectedComponent}
              onChange={(e) => { setSelectedComponent(Number(e.target.value)); setSimulationResult(null); }}
              displayEmpty
              sx={{ minWidth: 250 }}
            >
              <MenuItem value={0}
                disabled>{t('costing.selectIngredient')}</MenuItem>
              {recipe.components.map((c) => (
                <MenuItem key={c.id}
                  value={c.id}>{c.componentDescription}</MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              type="number"
              label={t('costing.priceChange')}
              value={priceChange}
              onChange={(e) => { setPriceChange(Number(e.target.value)); setSimulationResult(null); }}
              sx={{ width: 120 }}
              inputProps={{ step: 5 }}
            />
            <Button variant="primary"
              onClick={handleSimulate}
              disabled={!selectedComponent || isSimulating}>
              {t('costing.simulate')}
            </Button>
          </Stack>

          {simulationResult && (
            <Stack direction="row"
              spacing={4}
              sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.originalCost')}</Typography>
                <Typography variant="textXL">{formatCurrency(simulationResult.originalCost)}</Typography>
              </Box>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.simulatedCost')}</Typography>
                <Typography variant="textXL"
                  color={simulationResult.simulatedCost > simulationResult.originalCost ? 'error.main' : 'success.main'}>
                  {formatCurrency(simulationResult.simulatedCost)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.difference')}</Typography>
                <Typography variant="textXL"
                  color={simulationResult.difference > 0 ? 'error.main' : 'success.main'}>
                  {simulationResult.difference > 0 ? '+' : ''}{formatCurrency(simulationResult.difference)}
                </Typography>
              </Box>
              <Divider orientation="vertical"
                flexItem />
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.impactAcrossRecipes')}</Typography>
                <Typography variant="textXL">{simulationResult.affectedRecipes} {t('title').toLowerCase()}</Typography>
              </Box>
            </Stack>
          )}
        </Paper>
      )}

      {/* Margin Simulation */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>
          <AttachMoney sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('costing.marginSimulation')}
        </Typography>
        <Stack direction="row"
          spacing={2}
          alignItems="center">
          <TextField
            size="small"
            type="number"
            label={t('costing.sellPrice')}
            value={sellPrice || ''}
            onChange={(e) => setSellPrice(Number(e.target.value))}
            sx={{ width: 150 }}
            inputProps={{ step: 0.5, min: 0 }}
          />
          {sellPrice > 0 && (
            <Stack direction="row"
              spacing={3}
              sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.grossProfit')}</Typography>
                <Typography variant="textLG"
                  fontWeight={600}
                  color={sellPrice - totalCost > 0 ? 'success.main' : 'error.main'}>
                  {formatCurrency(sellPrice - totalCost)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="textXS"
                  color="text.secondary">{t('costing.marginPercent')}</Typography>
                <Typography variant="textLG"
                  fontWeight={600}
                  color={((sellPrice - totalCost) / sellPrice) * 100 > 0 ? 'success.main' : 'error.main'}>
                  {(((sellPrice - totalCost) / sellPrice) * 100).toFixed(1)}%
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Supplier Comparison */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>
          <CompareArrows sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('costing.supplierComparison')}
        </Typography>
        {supplierLoading ? (
          <Typography color="text.secondary">Loading...</Typography>
        ) : supplierData.filter((s) => s.suppliers.length > 0).length === 0 ? (
          <Typography color="text.secondary">No supplier data available</Typography>
        ) : (
          <Stack spacing={2}>
            {supplierData
              .filter((s) => s.suppliers.length > 0)
              .map((item) => (
                <Box key={item.componentId}>
                  <Typography variant="textSM"
                    fontWeight={600}
                    sx={{ mb: 0.5 }}>{item.componentDescription}</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('costing.supplier')}</TableCell>
                          <TableCell align="right">{t('costing.price')}</TableCell>
                          <TableCell align="right">{t('costing.packSize')}</TableCell>
                          <TableCell align="right">{t('costing.costPerUnit')}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {item.suppliers.map((s) => (
                          <TableRow key={s.supplierId}
                            sx={{ bgcolor: s.isPreferred ? 'success.light' : undefined }}>
                            <TableCell>{s.supplierName}</TableCell>
                            <TableCell align="right">{formatCurrency(s.itemPrice)}</TableCell>
                            <TableCell align="right">{s.packSize}{s.packUom}</TableCell>
                            <TableCell align="right">{formatCurrency(s.costPerUnit)}/kg</TableCell>
                            <TableCell>
                              {s.isPreferred && (
                                <Chip label={t('costing.preferred')}
                                  size="small"
                                  color="success" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default CostingTab;
